import { useState, useEffect } from "react";
import { Autocomplete, TextField } from "@mui/material";
import debounce from "lodash/debounce";
import axios from "axios";
import { InternalUser } from "../types/app";

const fetchUsers = async (search: string): Promise<InternalUser[]> => {
  try {
    const { data } = await axios.get(`/api/clientphonebook/internalusers/searchSelectUsers?searchText=${search}`);
    return data;
  } catch (error) {
    throw new Error("Failed to fetch users");
  }
};
const fetchAccountRepUsers = async (search: string): Promise<InternalUser[]> => {
  try {
    const { data } = await axios.get(`/api/clientphonebook/internalusers/searchAccountReps?searchText=${search}`);
    return data;
  } catch (error) {
    throw new Error("Failed to fetch users");
  }
};

type LimitOptions =
  | { limitToSalesReps?: false; limitToAccountReps?: false }
  | { limitToSalesReps: true; limitToAccountReps?: false }
  | { limitToSalesReps?: false; limitToAccountReps: true };

type CommonProps = LimitOptions & {
  label?: string;
  sx?: any;
  onBlur?: () => void;
  size?: "small" | "medium";
  limitTags?: number;
  getOptionDisabled?: (option: InternalUser) => boolean;
  disabled?: boolean;
};

type SingleProps = CommonProps & {
  multiple?: false;
  value: InternalUser | null;
  onChange: (user: InternalUser | null) => void;
};

type MultiProps = CommonProps & {
  multiple: true;
  value: InternalUser[];
  onChange: (users: InternalUser[]) => void;
};

type InternalUserAutocompleteProps = SingleProps | MultiProps;

const InternalUserAutocomplete = ({
  value,
  onChange,
  label = "",
  sx = {},
  multiple,
  onBlur,
  size,
  limitTags = 1,
  limitToSalesReps,
  limitToAccountReps,
  getOptionDisabled,
  disabled
}: InternalUserAutocompleteProps) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<InternalUser[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchOptions = async (value: string) => {
    setLoading(true);
    try {
      const users = limitToAccountReps ? await fetchAccountRepUsers(value) : await fetchUsers(value);
      const filteredUsers = limitToSalesReps ? users.filter((u) => u.salesRepId) : users;
      setOptions(filteredUsers);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedInputChange = debounce(setInputValue, 300);

  useEffect(() => {
    if (inputValue) {
      fetchOptions(inputValue);
    } else {
      setOptions([]);
    }
  }, [inputValue]);


  if (multiple) {
    return (
      <Autocomplete<InternalUser, true, false, false>
        multiple
        limitTags={limitTags}
        disabled={disabled}
        loading={loading}
        options={options}
        value={value as InternalUser[]}
        onChange={(_, newValue) => onChange(newValue as InternalUser[])}
        onBlur={onBlur}
        getOptionLabel={(option) => option.fullName || ""}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        getOptionDisabled={getOptionDisabled}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            sx={sx}
            label={label || "Search Users"}
            size={size || "small"}
            inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
            onChange={(event) => debouncedInputChange(event.target.value)}
          />
        )}
      />
    );
  }

  return (
    <Autocomplete<InternalUser, false, false, false>
      limitTags={limitTags}
      disabled={disabled}
      loading={loading}
      options={options}
      value={value as InternalUser | null}
      onChange={(_, newValue) => onChange(newValue as InternalUser | null)}
      onBlur={onBlur}
      getOptionLabel={(option) => option.fullName || ""}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionDisabled={getOptionDisabled}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          sx={sx}
          label={label || "Search Users"}
          size={size || "small"}
          inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
          onChange={(event) => debouncedInputChange(event.target.value)}
        />
      )}
    />
  );
};

export default InternalUserAutocomplete;
